import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  CcfToast,
  updateActiveContact,
} from '@nice-ccf/ui-components';
import styles from './ccf-consumer-card-details-tab.module.scss';
import {
  CcfTypography,
  CcfBox,
  CcfButton,
  CcfStrokeIcon,
  CcfUpArrowIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { FormGroup, FormControlLabel, Checkbox } from '@mui/material';

interface CcfConsumerCardDetailsTabCustomGeneralRowProps {
  headerCaption: string;
  detailsMinValue: boolean;
}

/**
 * Handles the click event when the edit button is clicked.
 * Sets the `isEditing` state to `true`.
 * @example
 */
function CcfConsumerCardDetailsTabCustomGeneralRow(
  props: CcfConsumerCardDetailsTabCustomGeneralRowProps
) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const activeContact = useSelector(activeContactSelector);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [formData, setFormData] = useState({
    accountNumber: activeContact?.customer.accountNumber || '',
    accountType: activeContact?.customer.accountLevel || '',
    salutation: activeContact?.customer.salutation || '',
    title: activeContact?.customer.title || '',
    firstName: activeContact?.customer.firstName || '',
    lastName: activeContact?.customer.lastName || '',
    dateOfBirth: activeContact?.customer.dateOfBirth || '',
    ssn: activeContact?.customer.ssn || '',
    balance: activeContact?.customer.balance || '',
    nextPayment: activeContact?.customer.nextPayment || '',
    customerSince: activeContact?.customer.customerSince || '',
    contactNumber: activeContact?.customer.phoneNo || '',
    customerType: activeContact?.customer.accountLevel || '',
  });
  const [initialFormData, setInitialFormData] = useState(formData);
  const dispatch = useDispatch();
  const [translate] = useTranslator();

  useEffect(() => {
    setInitialFormData(formData);
  }, [activeContact]);
  /**
   * Handles the click event when the edit button is clicked.
   * Sets the `isEditing` state to `true`.
   * @example
   */
  const handleEditClick = () =>  {
    if (isEditing) {
      // Reset form data to initial state if cancel button is clicked
      setFormData(initialFormData);
    }
    setIsEditing(!isEditing);
  };
  /**
   * Handles the click event for the save button.
   * Sets the `isEditing` state to `false`.
   * @example
   */
  const handleSaveClick = () => {
    setIsEditing(false);
    //let existingCustomerProperties = Object.assign({}, activeContact?.customer);
    const existingCustomerProperties = {
      ...activeContact?.customer,
      ...formData,
    };
    dispatch(
      updateActiveContact({
        id: activeContact?.id || '',
        updates: { customer: existingCustomerProperties },
      })
    );
    setShowToast(true);
    setToastMessage(translate('Generaldetailssavedsuccessfully'));
    setTimeout(() => {
      setShowToast(false);
      setToastMessage('');
    }, 3000);
  };

  /**
   * Handles the click event for expanding or collapsing the custom address row.
   * @example
   */
  const handleExpandCollapseClick = () => setIsExpanded(!isExpanded);

  /**
   * Handles the change event for input elements.
   * Updates the form data with the new value.
   * @param e - The change event.
   * @example
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  /**
   * Handles the change event for select elements.
   * Updates the form data with the new value.
   * @param e - The change event.
   * @example
   */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      dateOfBirth: value,
    });
  };

  /**
   * Handles the change event for select elements.
   * Updates the form data with the new value.
   * @param e - The change event.
   * @example
   */
  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      accountType: value,
    });
  };

  return (
    <div className={styles.consumerCardDetails} id="detailsTab_general_panel">
      <div className={styles.header} style={{ height: '40px' }}>
        <div style={{ display: 'flex' }}>
          {isEditing && <CcfStrokeIcon className='pointerEvent'/>}
          <CcfTypography className={styles.captionHeader}>
            {translate(props.headerCaption)}
          </CcfTypography>
        </div>
        <CcfBox
          display={'flex'}
          style={{ width: isEditing ? '150px' : '100px' }}
          justifyContent={'space-around'}
        >
          {isEditing ? (
            <CcfBox
              display={'flex'}
              width={'125px'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <CcfButton
                id="generalTab_BtnCancel"
                className={styles.btnStyles}
                onClick={handleEditClick}
              >
                {translate('cancel')}
              </CcfButton>
              <CcfButton
                id="generalTab_BtnSave"
                className={styles.btnStyles}
                primary
                onClick={handleSaveClick}
              >
                {translate('save')}
              </CcfButton>
            </CcfBox>
          ) : (
            <>
              {isExpanded && (
                <CcfStrokeIcon
                  id="generalTab_BtnCancel"
                  className={styles.alginIcon}
                  onClick={handleEditClick}
                />
              )}
              <CcfUpArrowIcon
                id="generalTab_BtnExpand"
                className='pointerEvent'
                style={{ transform: 'rotate(180deg)' }}
                onClick={handleExpandCollapseClick}
              />
            </>
          )}
        </CcfBox>
      </div>
      {isExpanded && (
        <div
          className={styles.body}
          style={{ backgroundColor: isEditing ? '#ECF3F8' : '#FAFAFA' }}
        >
          <div
            style={{
              width: isEditing
                ? props.detailsMinValue
                  ? '90%'
                  : '75%'
                : props.detailsMinValue
                ? '100%'
                : '90%',
            }}
            className={styles.bodyContainer}
          >
            {isEditing && (
              <div className={styles.column}>
                <FormGroup>
                  <FormControlLabel
                    className={styles.checkBoxLabel}
                    control={<Checkbox id="activeCheckBox" defaultChecked />}
                    label="Active"
                  />
                </FormGroup>
              </div>
            )}
            <CcfBox display={'flex'}>
              <div className={styles.column}>
                {renderField(
                  'FullNAME',
                  `${formData.salutation} ${formData.firstName} ${formData.lastName} (He/Him)`,
                  'AccountNumber',
                  'accountNumber',
                  formData.accountNumber,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'ACCOUNTLEVEL',
                  formData.accountType,
                  'Salutation',
                  'salutation',
                  formData.salutation,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'BALANCE',
                  formData.balance,
                  'FirstName',
                  'firstName',
                  formData.firstName,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'DATEOFBIRTH',
                  formData.dateOfBirth,
                  'SSN',
                  'ssn',
                  formData.ssn,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
              </div>
              <div className={styles.column}>
                {renderField(
                  'CUSTOMERSINCE',
                  formData.customerSince,
                  'AccountType',
                  'accountType',
                  formData.accountType,
                  isEditing,
                  handleSelectChange,
                  props.detailsMinValue,
                  true
                )}
                {renderField(
                  'ACCOUNT',
                  formData.accountNumber,
                  'Title',
                  'title',
                  formData.title,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'NEXTPAYMENT',
                  formData.nextPayment,
                  'LastName',
                  'lastName',
                  formData.lastName,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'PREFERREDCONTACTMETHOD',
                  formData.contactNumber,
                  'DateOfBirth',
                  'dateOfBirth',
                  formData.dateOfBirth,
                  isEditing,
                  handleDateChange,
                  props.detailsMinValue
                )}
              </div>
            </CcfBox>
          </div>
        </div>
      )}
      <CcfBox>
        <CcfToast open={showToast} type={'success'} message={toastMessage} />
      </CcfBox>
    </div>
  );
}

/**
 * Renders a field with label and value, along with an input or select element for editing.
 * @example
 * @param label - The label for the field.
 * @param value - The value to display for the field.
 * @param name - The name attribute for the input/select element.
 * @param inputValue - The current value of the input/select element.
 * @param isEditing - A boolean indicating whether the field is in editing mode.
 * @param handleChange - The event handler for the input/select element's change event.
 * @param isSelect - A boolean indicating whether the field should render a select element instead of an input element.
 * @returns The rendered field component.
 */
function renderField(
  label: string,
  value: string,
  onEditCaption: string,
  name: string,
  inputValue: string,
  isEditing: boolean,
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  detailsMinValue: boolean,
  isSelect = false
) {
  return (
    <div className={styles.field} style={{ marginRight: '10Px' }}>
      {!isEditing ? (
        <>
          <CcfTypography
            className={
              detailsMinValue ? styles.captionlabelMinView : styles.captionlabel
            }
            translationKey={label.toUpperCase()}
          >
          </CcfTypography>
          <CcfTypography
            className={
              detailsMinValue ? styles.captionValueMinView : styles.captionValue
            }
          >
            {value}
          </CcfTypography>
        </>
      ) : (
        <>
          <CcfTypography className={styles.editModeCaptionLabel} translationKey={onEditCaption.toUpperCase()}>
          </CcfTypography>
          {isSelect ? (
            <select
              id={name}
              className={styles.selectControl}
              name={name}
              value={inputValue}
              onChange={() => handleChange}
            >
              <option value={inputValue}>{inputValue}</option>
            </select>
          ) : name === 'dateOfBirth' ? (
            <input
              className={styles.inputControl}
              id={name}
              type="date"
              name={name}
              value={
                inputValue
                  ? new Date(inputValue).toISOString().split('T')[0]
                  : ''
              }
              onChange={handleChange}
            />
          ) : (
            <input
              className={styles.inputControl}
              id={name}
              disabled={name === 'accountNumber' ? true : false}
              type={name === 'ssn' ? 'password' : 'text'}
              name={name}
              value={inputValue}
              onChange={handleChange}
            />
          )}
        </>
      )}
    </div>
  );
}

export default CcfConsumerCardDetailsTabCustomGeneralRow;
